import { RouteObject, useRouteError } from "react-router-dom";

import { PermissionAction } from "../../model/permissions/types/PermissionAction";
import { PermissionType } from "../../model/permissions/types/PermissionType";
import { AtiraRoute } from "../AtiraRoute";
import { AtiraRouteAdmin } from "../AtiraRouteAdmin";
import { Layout } from "../Layout";
import { AdminSearchUsersRouteWithLazy } from "../admin/routes/search/AdminSearchRouteWithLazy";
import { EmailCampaignsRoute } from "../campaign/EmailCampaignsRoute";
import { DashboardRouteWithLazy } from "../dashboard/DashboardRouteWithLazy";
import { EntriesRouteWithLazy } from "../entries/EntriesRouteWithLazy";
import { FolderFormsRouteWithLazy } from "../form/FolderFormsRouteWithLazy";
import { GoogleDriveRouteWithLazy } from "../integration/GoogleDriveRouteWithLazy";
import { IntegrationRouteWithLazy } from "../integration/IntegrationRouteWithLazy";
import { WhatsAppAIChatReviewRoute } from "../integration/WhatsAppAIChatReviewRoute";
import { KnabanRouteWithLazy } from "../kanban/KanbanRouteWithLazy";
import { PermissionsRoute } from "../permissions/PermissionsRoute";
import { ProfileEdit } from "../profile/ProfileEdit";
import { Reports } from "../reports/Reports";

import { SubscriptionRoute } from "../subscription/SubscriptionRoute";
import { TaskRouteWithLazy } from "../tasks/TaskRouteWithLazy";
import { UserRoles } from "../../model/user/types/UserRoles";
import { ModuleInstances } from "../modules/module-instance/ModuleInstances";
import { SettingsRouteWithLazy } from "../settings/SettingsRouteWithLazy";
import { SearchRouteWithLazy } from "../search/SearchRouteWithLazy";
import { AdminRouteWithLazy } from "../admin/routes/admin/AdminRouteWithLazy";
import { AdminPSARouteWithLazy } from "../admin/routes/psa/AdminPSARouteWithLazy";
import { AdminRoadmapRouteWithLazy } from "../admin/routes/roadmap/AdminRoadmapRouteWithLazy";
import { GlobalFormProviders } from "../GlobalFormProviders";
import { FormsAndFoldersRouteWithLazy } from "../form/FormsAndFoldersRouteWithLazy";
import styled from "styled-components";
import { AtiraImage } from "../../components/Image";
import { Images } from "../../assets";
import { Button } from "../../components/Button";
import { Text } from "../../components/Text";
import capitalize from "lodash/capitalize";
import { ThemeKeys, useTheme } from "../../ThemeContext";
import { useEffect } from "react";
import { HelpSupportRouteWithLazy } from "../help/HelpSupportRouteWithLazy";
import { useTranslation } from "react-i18next";
import { WhatsAppMetaRoute } from "../integration/WhatsAppMetaRoute";
import { OrgChatRoute } from "../chat/OrgChatRoute";

const ErrorWrapper = styled.div`
  padding: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
`;

function handleMissingChunkError(error: unknown) {
  const flag = window.localStorage.getItem(
    "atiraspace-chunk-load-error-reloaded",
  );

  if (flag !== null) {
    // Already reloaded once, do not reload again to prevent infinite loop
    return;
  }

  const isChunkError =
    error && (error as any)?.message?.includes("Loading chunk");

  if (isChunkError) {
    window.localStorage.setItem("atiraspace-chunk-load-error-reloaded", "true");

    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
}

const ChunkErrorFallback = () => {
  const error = useRouteError();
  const { t } = useTranslation();

  const { selectedTheme } = useTheme();
  // fallback for older saved themes/saved themes
  const currentTheme =
    (selectedTheme as any) === "light" ? ThemeKeys.PURPLE : selectedTheme;

  useEffect(() => {
    handleMissingChunkError(error);
  }, [error]);

  const image =
    Images[
      `AtiraSpaceLogoBeta${capitalize(currentTheme)}` as keyof typeof Images
    ];

  return (
    <ErrorWrapper>
      <Text as="h2" color="main">
        {t("common.error.something_wrong")}
      </Text>
      <Text fontSize="s" color="darkerSub">
        {t("common.error.reload_page")}
      </Text>
      <Button hover onClick={() => window.location.reload()}>
        {t("common.reload")}
      </Button>
      <div>
        <AtiraImage src={image} width={"22rem"} />
      </div>
    </ErrorWrapper>
  );
};

export const PrivateRouter: RouteObject[] = [
  {
    element: (
      <GlobalFormProviders>
        <Layout />
      </GlobalFormProviders>
    ),
    errorElement: <ChunkErrorFallback />,
    children: [
      {
        path: "/dashboard",
        element: <AtiraRoute component={<DashboardRouteWithLazy />} />,
      },
      {
        path: "/modules/:id",
        element: <AtiraRoute component={<ModuleInstances />} />,
      },
      {
        path: "/forms",
        element: (
          <AtiraRoute
            component={<FormsAndFoldersRouteWithLazy />}
            withPermissions={[
              {
                type: PermissionType.FORM,
                action: PermissionAction.READ,
              },
            ]}
          />
        ),
      },
      {
        path: "/forms/:folderSlug",
        element: (
          <AtiraRoute
            component={<FolderFormsRouteWithLazy />}
            withPermissions={[
              {
                type: PermissionType.FORM,
                action: PermissionAction.READ,
              },
            ]}
          />
        ),
      },
      {
        path: "/entries",
        element: (
          <AtiraRoute
            component={<EntriesRouteWithLazy />}
            withPermissions={[
              {
                type: PermissionType.ENTRY,
                action: PermissionAction.READ,
              },
            ]}
          />
        ),
      },
      {
        path: "/deals",
        element: (
          <AtiraRoute
            component={<KnabanRouteWithLazy />}
            withPermissions={[
              {
                type: PermissionType.KANBAN,
                action: PermissionAction.READ,
              },
            ]}
          />
        ),
      },
      {
        path: "/reports",
        element: (
          <AtiraRoute
            component={<Reports />}
            notAllowedRoles={[UserRoles.MEMBER]}
          />
        ),
      },
      {
        path: "/tasks",
        element: (
          <AtiraRoute
            component={<TaskRouteWithLazy />}
            withPermissions={[
              {
                type: PermissionType.LEAD_TASK,
                action: PermissionAction.READ,
              },
              {
                type: PermissionType.KANBAN_TASK,
                action: PermissionAction.READ,
              },
              {
                type: PermissionType.CONTACT_TASK,
                action: PermissionAction.READ,
              },
              {
                type: PermissionType.ORPHAN_TASK,
                action: PermissionAction.READ,
              },
            ]}
          />
        ),
      },
      {
        path: "/campaigns",
        element: <AtiraRoute component={<EmailCampaignsRoute />} />,
      },
      {
        path: "/profile",
        element: <AtiraRoute component={<ProfileEdit />} />,
      },
      {
        path: "/subscription",
        element: <AtiraRoute component={<SubscriptionRoute />} />,
      },
      {
        path: "/permissions",
        element: <AtiraRoute component={<PermissionsRoute />} />,
      },
      {
        path: "/admin",
        element: <AtiraRouteAdmin component={<AdminRouteWithLazy />} />,
      },
      {
        path: "/admin/users",
        element: (
          <AtiraRouteAdmin component={<AdminSearchUsersRouteWithLazy />} />
        ),
      },
      {
        path: "/admin/psa",
        element: <AtiraRouteAdmin component={<AdminPSARouteWithLazy />} />,
      },
      {
        path: "/admin/roadmap",
        element: <AtiraRouteAdmin component={<AdminRoadmapRouteWithLazy />} />,
      },
      {
        path: "/integration/whatsapp-ai-chat/review",
        element: (
          <AtiraRoute
            component={<WhatsAppAIChatReviewRoute />}
            notAllowedRoles={[UserRoles.MEMBER]}
          />
        ),
      },
      {
        path: "/integration/whatsapp-meta",
        element: <AtiraRoute component={<WhatsAppMetaRoute />} />,
      },
      {
        path: "/integration/google-drive",
        // A member can't connect Drive, but does use the file manager once
        // the owner has - same shape as the WhatsApp inbox above.
        element: <AtiraRoute component={<GoogleDriveRouteWithLazy />} />,
      },
      {
        path: "/integration",
        // A member works the WhatsApp chats assigned to them from the inbox;
        // connecting and configuring integrations is the owner's
        element: (
          <AtiraRoute
            component={<IntegrationRouteWithLazy />}
            notAllowedRoles={[UserRoles.MEMBER]}
          />
        ),
      },
      {
        path: "/settings/:section",
        element: <AtiraRoute component={<SettingsRouteWithLazy />} />,
      },
      {
        path: "/team-chat",
        element: (
          <AtiraRoute
            component={<OrgChatRoute />}
            requiredRoles={[UserRoles.ORGANIZATION, UserRoles.MEMBER]}
          />
        ),
      },
      {
        path: "/search",
        element: <AtiraRoute component={<SearchRouteWithLazy />} />,
      },
      {
        path: "/help-support",
        element: <AtiraRoute component={<HelpSupportRouteWithLazy />} />,
      },
      {
        path: "/",
        element: <AtiraRoute redirect="/dashboard" />,
      },
    ],
  },
];
